import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Download as DownloadIcon, Visibility } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getValidToken } from "../../../utils/authUtils";
import {
  formatCurrency,
  formatDate,
  formatVehicleNumber,
} from "../../../utils/utils";
import { useReportFilter } from "../context/ReportFilterContext";
import { tableCellStyles, tableHeaderStyles } from "../../../utils/styles";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../../components/Loading";

interface LedgerEntry {
  "Trip ID": string;
  "Vehicle Number": string;
  "Company Name": string;
  "Trip Type": string;
  "Journey Start Date": string;
  "Journey End Date": string | null;
  "From Location": string;
  "To Location": string;
  "Bus Booking (Contract) Amount": string;
  "Bus Booking (Contract) Paid": string;
  "Bus Booking (Contract) Balance": string;
  "Bus Booking (Commission) Total": number;
  "Bus Booking (Commission) Received": number;
  "Bus Booking (Commission) Amount": number;
  "Bus Booking (Commission) Net": number;
  "Bus Booking (Commission) Pending": number;
  "Luggage Booking (Contract) Amount": string;
  "Luggage Booking (Contract) Paid": string;
  "Luggage Booking (Contract) Balance": string;
  "Luggage Booking (Commission) Total": string;
  "Luggage Booking (Commission) Paid": string;
  "Luggage Booking (Commission) ToPay": string;
  "Luggage Booking Commission": string;
  "Luggage Booking (Settlement Payments)": string;
  "Luggage Booking (Pickup) Total": string;
  "Luggage Booking (Pickup) Paid": string;
  "Luggage Booking (Pickup) ToPay": string;
  "Luggage Booking (Pickup) Commission": string;
  "Luggage Booking (Pickup) (Settlement Payments)": string;
  "Driver Expense": string;
  "Cleaner Expense": string;
  "Toll Expense": string;
  "Parking Expense": string;
  "RTO Expense": string;
  "Food Expense": string;
  "Other Expense": string;
  "Trip Expense Advance Paid": string;
  "Trip Expense Settlement Paid": string;
  "Total Trip Expense": string;
  "Fuel Expense": string;
  "Fuel Quantity": string;
  "Fuel Advance Paid": string;
  "Fuel Settlement Paid": string;
  "Maintenance Expense": string;
  "Maintenance Advance Paid": string;
  "Maintenance Settlement Paid": string;
  "Advance Cash Given": string;
  "Advance Cash Used": string;
  "Advance Cash Balance": string;
  "Advance Balance Recovered": string;
  "Advance Balance Force Recovered": boolean;
  "Total Expense": string;
  "Total Income Expected": number;
  "Total Income Received": number;
  "Total Income Pending": number;
  "Total Advance Paid": string;
  "Total Settlement Paid": string;
  "Total Commission Amount": number;
  "P&L": number;
}

interface LedgerSummary {
  totalTrips: number;
  totalIncomeExpected: string;
  totalIncomeReceived: string;
  totalIncomePending: string;
  busContractAmount: string;
  busContractPaid: string;
  busContractBalance: string;
  busCommissionTotal: string;
  busCommissionReceived: string;
  busCommissionNet: string;
  busCommissionPending: string;
  busCommissionAmount: string;
  luggageContractAmount: string;
  luggageContractPaid: string;
  luggageContractBalance: string;
  luggageCommissionTotal: string;
  luggageCommissionPaid: string;
  luggageCommissionToPay: string;
  luggageCommissionAmount: string;
  totalExpenses: string;
  totalTripExpenses: string;
  totalFuelExpenses: string;
  totalMaintenanceExpenses: string;
  totalAdvancePaid: string;
  totalSettlementPaid: string;
  totalCommissionAmount: string;
  totalProfitLoss: string;
}

interface ReportInfo {
  companyName: string;
  startDate: string;
  endDate: string;
  filterType: string;
  totalRecords: number;
  generatedAt: string;
}

interface LedgerReportData {
  reportInfo: ReportInfo;
  summary: LedgerSummary;
  ledgerData: LedgerEntry[];
}

const LedgerTableRow: React.FC<{ entry: LedgerEntry }> = ({ entry }) => {
  const navigate = useNavigate();
  return (
    <TableRow hover>
      <TableCell sx={{ ...tableCellStyles }}>
        <Tooltip title="View Trip">
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              cursor: "pointer",
              textDecoration: "underline",
              color: "primary.main",
            }}
            onClick={() => {
              navigate(`/trip/daily-bus/${entry["Trip ID"]}/basic-details`);
            }}
          >
            {formatDate(entry["Journey Start Date"])}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">{entry["From Location"]}</Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">
          {entry["Journey End Date"]
            ? formatDate(entry["Journey End Date"])
            : "-"}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">{entry["To Location"]}</Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles }}>
        <Typography variant="caption">
          {formatVehicleNumber(entry["Vehicle Number"])}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}>
        <Typography variant="caption">{entry["Trip Type"]}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Bus Booking (Contract) Amount"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Bus Booking (Contract) Paid"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Bus Booking (Contract) Balance"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(entry["Bus Booking (Commission) Total"])}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(entry["Bus Booking (Commission) Received"])}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(entry["Bus Booking (Commission) Amount"])}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(entry["Bus Booking (Commission) Net"])}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(entry["Bus Booking (Commission) Pending"])}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Contract) Amount"])
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Luggage Booking (Contract) Paid"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Contract) Balance"])
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Commission) Total"])
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Commission) Paid"])
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Commission) ToPay"])
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Luggage Booking Commission"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Settlement Payments)"])
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Luggage Booking (Pickup) Total"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Luggage Booking (Pickup) Paid"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Luggage Booking (Pickup) ToPay"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Pickup) Commission"])
          )}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(
            parseFloat(entry["Luggage Booking (Pickup) (Settlement Payments)"])
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Driver Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Cleaner Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Toll Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Parking Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["RTO Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Food Expense"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Other Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Trip Expense Advance Paid"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Trip Expense Settlement Paid"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Total Trip Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Fuel Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption">
          {parseFloat(entry["Fuel Quantity"]).toFixed(2)} L
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Fuel Advance Paid"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Fuel Settlement Paid"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Maintenance Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Maintenance Advance Paid"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Maintenance Settlement Paid"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Advance Cash Given"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Advance Cash Used"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Advance Balance Recovered"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Advance Cash Balance"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Total Expense"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Total Advance Paid"]))}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(parseFloat(entry["Total Settlement Paid"]))}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography variant="caption" sx={{ fontSize: "10px" }}>
          {formatCurrency(entry["Total Commission Amount"])}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          {formatCurrency(entry["Total Income Expected"])}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ ...tableCellStyles }}>
        <Typography
          variant="caption"
          sx={{ color: "success.main", fontWeight: 500 }}
        >
          {formatCurrency(entry["Total Income Received"])}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ ...tableCellStyles, borderRight: "1px solid #e0e0e0" }}
      >
        <Typography
          variant="caption"
          sx={{ color: "warning.main", fontWeight: 500 }}
        >
          {formatCurrency(entry["Total Income Pending"])}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{
          minWidth: 100,
          position: "sticky",
          right: 0,
          bgcolor: "white",
          zIndex: 2,
          borderLeft: "1px solid #e0e0e0",
          boxShadow: "-1px 0 4px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: entry["P&L"] >= 0 ? "success.main" : "error.main",
          }}
        >
          {formatCurrency(entry["P&L"])}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const LedgerReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const [displayedRows, setDisplayedRows] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { timeFilter, customStartDate, customEndDate, selectedVehicles } =
    filterState;

  // Reset displayed rows when filters change
  useEffect(() => {
    setDisplayedRows(20);
  }, [timeFilter, customStartDate, customEndDate, selectedVehicles]);

  const {
    data: reportData,
    isLoading,
    isFetching,
    isError,
  } = useQuery<{
    data: LedgerReportData;
    isSuccess: boolean;
    message: string;
  }>({
    queryKey: [
      "ledgerReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/ledger/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/ledger/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedVehicles.length > 0) {
        params.vehicleNumbers = selectedVehicles;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  const handleShowMore = () => {
    setIsLoadingMore(true);
    // Simulate loading delay
    setTimeout(() => {
      setDisplayedRows((prev) => prev + 20);
      setIsLoadingMore(false);
    }, 500);
  };

  const { ledgerData, reportInfo } = reportData?.data ?? {
    ledgerData: [],
    reportInfo: { companyName: "" },
  };

  const displayedData = ledgerData?.slice(0, displayedRows);
  const hasMoreData = displayedRows < ledgerData?.length;

  return (
    <Box sx={{ position: "relative" }}>
      {/* Ledger Data Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "0.9rem", fontWeight: 500, p: 1.5 }}
        >
          {reportInfo.companyName} Ledger Report
        </Typography>

        {isLoading && (
          <Loading fullScreen={false} message="Loading ledger report..." />
        )}
        {isFetching && !isLoading && (
          <Loading fullScreen={false} message="Refreshing ledger report..." />
        )}
        {ledgerData.length === 0 ? (
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              color: "text.secondary",
              px: 1.5,
              pb: 1,
            }}
          >
            No ledger data available
          </Typography>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                px: 1.5,
                pb: 1,
              }}
            >
              Showing {displayedData.length} of {ledgerData.length} trips
            </Typography>
            <TableContainer
              sx={{
                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: "#a8a8a8",
                  },
                },
                "&::-webkit-scrollbar-corner": {
                  backgroundColor: "#f1f1f1",
                },
              }}
            >
              <Table
                stickyHeader
                size="small"
                aria-label="ledger report table"
                sx={{
                  tableLayout: "auto",
                  "& .MuiTableCell-root": {
                    padding: "4px 8px",
                    fontSize: "0.75rem",
                  },
                }}
              >
                <TableHead>
                  {/* First header row - Group headers */}
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      Trip Start Date
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      From Location
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      Trip End Date
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      To Location
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 120,
                      }}
                    >
                      Vehicle
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Trip Type
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Bus Booking Contract
                    </TableCell>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Bus Booking Commission
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Luggage Contract
                    </TableCell>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Luggage Commission
                    </TableCell>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Luggage Pickup
                    </TableCell>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Trip Expenses
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Trip Payments
                    </TableCell>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Fuel
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Maintenance
                    </TableCell>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Advance Cash
                    </TableCell>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Totals
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Income
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.75rem",
                        position: "sticky",
                        right: 0,
                        bgcolor: "#f5f5f5",
                        zIndex: 3,
                        borderLeft: "1px solid #e0e0e0",
                        boxShadow: "-1px 0 4px rgba(0,0,0,0.1)",
                        minWidth: 100,
                        textAlign: "center",
                      }}
                    >
                      P&L
                    </TableCell>
                  </TableRow>

                  {/* Second header row - Sub headers */}
                  <TableRow>
                    {/* Bus Booking Contract */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Paid
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Balance
                    </TableCell>

                    {/* Bus Booking Commission */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Received
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Net
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Pending
                    </TableCell>

                    {/* Luggage Contract */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Paid
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Balance
                    </TableCell>

                    {/* Luggage Commission */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Paid
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      ToPay
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Commission
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Settlement
                    </TableCell>

                    {/* Luggage Pickup */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Paid
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      ToPay
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Commission
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Settlement
                    </TableCell>

                    {/* Trip Expenses */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Driver
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Cleaner
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 70,
                      }}
                    >
                      Toll
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Parking
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 70,
                      }}
                    >
                      RTO
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 70,
                      }}
                    >
                      Food
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Other
                    </TableCell>

                    {/* Trip Payments */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Advance
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Settlement
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Total
                    </TableCell>

                    {/* Fuel */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Expense
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 80,
                      }}
                    >
                      Quantity
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Advance
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Settlement
                    </TableCell>

                    {/* Maintenance */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Expense
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Advance
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Settlement
                    </TableCell>

                    {/* Advance Cash */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Given
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Used
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Recovered
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Balance
                    </TableCell>

                    {/* Totals */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Expense
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Advance
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 90,
                      }}
                    >
                      Settlement
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Commission
                    </TableCell>

                    {/* Income */}
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Expected
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                      }}
                    >
                      Received
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...tableHeaderStyles,
                        fontSize: "0.7rem",
                        minWidth: 100,
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      Pending
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedData.map((entry, index) => (
                    <LedgerTableRow
                      key={`${entry["Trip ID"]}-${index}`}
                      entry={entry}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hasMoreData && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                <Typography
                  variant="caption"
                  onClick={handleShowMore}
                  sx={{
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  {isLoadingMore ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading more...
                    </>
                  ) : (
                    `Show More (${ledgerData.length - displayedRows} remaining)`
                  )}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LedgerReport;
